/**
 * Drop-zone view. Renders differently per phase:
 *   EMPTY    → big drop zone, "Drop one or two .flp files"
 *   HAS_A    → small drop zone ("Drop second .flp") + chip for A
 *   HAS_BOTH → chips for both + reset button, no drop target
 *
 * Installs document-level drag/drop listeners once at module init so
 * the user can drop files anywhere on the page.
 */

import { addFiles, removeA, type State, type LoadedFile } from "./state.ts";
import { DEMOS, loadDemo } from "./demos.ts";
import { h, formatBytes, on } from "./ui.ts";

export function renderDropzone(state: State): HTMLElement {
  if (state.phase === "EMPTY") return renderEmpty();
  if (state.phase === "HAS_A") return renderPartial(state.a!);
  // HAS_BOTH: info panels below carry the filenames + remove buttons.
  return h("div", { class: "dropzone-wrap dropzone-wrap--hidden" });
}

function renderEmpty(): HTMLElement {
  const input = h("input", {
    type: "file",
    accept: ".flp",
    multiple: true,
    class: "file-input",
    id: "file-input",
    "aria-label": "Choose .flp files",
  });
  on(input, "change", () => {
    if (input.files) void addFiles(Array.from(input.files));
    input.value = "";
  });

  const label = h("label", { for: "file-input", class: "dropzone dropzone--big" }, [
    h("p", { class: "dropzone__title" }, "Drop one or two .flp files"),
    h("p", { class: "dropzone__hint" }, "or click to browse"),
  ]);

  const children: HTMLElement[] = [label, input, renderPrivacyNote()];
  if (DEMOS.length > 0) children.push(renderDemoLinks());
  return h("div", { class: "dropzone-wrap" }, children);
}

function renderPrivacyNote(): HTMLElement {
  // Inline SVG via innerHTML — h() is HTML-only, doesn't take "svg".
  const note = h("p", { class: "privacy-note" });
  note.innerHTML =
    '<svg class="privacy-note__icon" viewBox="0 0 16 16" width="12" height="12" aria-hidden="true">' +
    '<path d="M5 7V5a3 3 0 0 1 6 0v2h.5a1 1 0 0 1 1 1v5.5a1 1 0 0 1-1 1h-7a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1H5Zm1 0h4V5a2 2 0 1 0-4 0v2Z" fill="currentColor"/>' +
    "</svg>" +
    "<span>Files are parsed in your browser. Nothing is uploaded.</span>";
  return note;
}

function renderDemoLinks(): HTMLElement {
  const links = DEMOS.map((pair) => {
    const btn = h("button", {
      type: "button",
      class: "demo-link",
      onclick: () => {
        // On the embedded viewer (/flpdiff) keep the user in context —
        // load the demo without changing the URL. Elsewhere, route to
        // /diff/{slug} so the diff is shareable / reloadable.
        const path = window.location.pathname;
        const onEmbedded = path === "/flpdiff" || path === "/flpdiff/";
        if (onEmbedded) {
          void loadDemo(pair);
          return;
        }
        const target = `/diff/${pair.slug}`;
        if (window.location.pathname !== target) {
          history.pushState({}, "", target);
          window.dispatchEvent(new PopStateEvent("popstate"));
          return;
        }
        void loadDemo(pair);
      },
    }, `Try demo: ${pair.slug}`);
    return btn;
  });
  return h("div", { class: "demo-links" }, [
    h("span", { class: "demo-links__hint" }, "or"),
    ...links,
  ]);
}

function renderPartial(a: LoadedFile): HTMLElement {
  const input = h("input", {
    type: "file",
    accept: ".flp",
    multiple: false,
    class: "file-input",
    id: "file-input-b",
    "aria-label": "Choose second .flp file",
  });
  on(input, "change", () => {
    if (input.files) void addFiles(Array.from(input.files));
    input.value = "";
  });

  return h("div", { class: "dropzone-wrap dropzone-wrap--paired" }, [
    chip(a, "before", removeA),
    h(
      "label",
      { for: "file-input-b", class: "dropzone dropzone--small" },
      h("p", { class: "dropzone__title" }, "Drop second .flp to compare"),
    ),
    input,
  ]);
}

function chip(file: LoadedFile, label: string, onRemove: () => void): HTMLElement {
  const remove = h("button", {
    type: "button",
    class: "chip__remove",
    "aria-label": `Remove ${label} file`,
    onclick: onRemove,
  }, "×");
  return h("span", { class: "chip" }, [
    h("span", { class: "chip__label" }, label),
    h("span", { class: "chip__name" }, file.name),
    h("span", { class: "chip__size" }, formatBytes(file.bytes)),
    remove,
  ]);
}

// ---- Document-level drag/drop wiring (init once) ----

let initialized = false;
export function initDragDrop(): void {
  if (initialized) return;
  initialized = true;

  const dropTarget = document.documentElement;

  let depth = 0;
  const setActive = (on: boolean) => {
    document.body.toggleAttribute("data-drag-active", on);
  };

  on(dropTarget as unknown as HTMLElement, "dragenter", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
    depth++;
    setActive(true);
  });
  on(dropTarget as unknown as HTMLElement, "dragover", (e) => {
    if (!hasFiles(e)) return;
    e.preventDefault();
  });
  on(dropTarget as unknown as HTMLElement, "dragleave", (e) => {
    if (!hasFiles(e)) return;
    depth = Math.max(0, depth - 1);
    if (depth === 0) setActive(false);
  });
  on(dropTarget as unknown as HTMLElement, "drop", (e) => {
    e.preventDefault();
    depth = 0;
    setActive(false);
    const files = Array.from(e.dataTransfer?.files ?? []);
    if (files.length > 0) void addFiles(files);
  });
}

function hasFiles(e: DragEvent): boolean {
  return Array.from(e.dataTransfer?.types ?? []).includes("Files");
}
